/**
 * F6 (process) command payloads, as sent in `commands[].payload` of POST /sync/commands.
 * Also used by the pack hall (F7): weigh_in, record_output and close_run run on the same pipeline.
 *
 * Wire names follow the spec's engineering.api command bodies (F6.9, F6.1, F6.10, F6.2, F6.3,
 * F6.4, F6.5, F6.7, F6-B1.b) with the gate's uniform rules:
 *  - UNITS: every spec field in kg is sent as INTEGER GRAMS with `_g` (`weight_read_kg` →
 *    `weight_read_g`); moisture is an exact decimal string with one decimal ("9.4").
 *  - `occurred_at`, `device_id` and the actor are the envelope's (offline/protocol.ts) and are not
 *    repeated. The spec's command `id` is the envelope id; where that id is also the id of the row
 *    the server creates (the run, the run input, the output sack, the shipment item, the sample)
 *    it is ALSO written in the payload under its own name, so a handler never has to guess.
 *  - Ids of things the server will create are UUIDv7s made on the phone.
 *
 * Importing this module registers every schema, so `enqueueCommand` refuses a malformed payload.
 */
import { z } from 'zod'
import { registerCommand } from '@/offline'

const id = z.string().min(1)
const isoTs = z.string().min(1)
const grams = z.number().int()
const payload = z.string().min(1)
const entry = z.enum(['SCAN', 'TYPED'])
/** Moisture % with one decimal, exact ("12.4"). */
const pctString = z.string().regex(/^\d{1,2}\.\d$/, 'percent with one decimal')

export const MIXING_REASONS = [
  'RAIN',
  'SPILL',
  'OTHER_FARMER',
  'MACHINE_NOT_EMPTY',
  'OTHER',
] as const
export type MixingReason = (typeof MIXING_REASONS)[number]

/** F6-B1.b cause list (exceptions.resolution_code). */
export const RUN_CAUSES = [
  'UNWEIGHED_SACK',
  'SCALE_ERROR',
  'TYPING_ERROR',
  'BAG_IN_WITHOUT_SCAN',
  'HIGH_MOISTURE',
  'MACHINE_FAULT',
  'BAND_WRONG',
  'UNEXPLAINED_LOSS',
  'MIXING',
] as const
export type RunCause = (typeof RUN_CAUSES)[number]

/** Corrections of a run's sacks and bags (a tag scanned or typed for each). */
export const CORRECTION_KINDS = [
  'REWEIGH_OUTPUT',
  'ADD_OUTPUT',
  'ADD_INPUT',
  'VOID_OUTPUT',
] as const
export type CorrectionKind = (typeof CORRECTION_KINDS)[number]
/**
 * Every correct_run kind: the sack kinds, and POUCH_COUNT — a closed PACK run's good pouches
 * (held SURPLUS, pack lot CLOSED), sent without a tag.
 */
export const RUN_CORRECTION_KINDS = [...CORRECTION_KINDS, 'POUCH_COUNT'] as const
export type RunCorrectionKind = (typeof RUN_CORRECTION_KINDS)[number]

export const CANCEL_REASONS = ['WRONG_MACHINE', 'WRONG_LOT', 'OTHER'] as const

// ---------------------------------------------------------------------------------------------
// F6.9 bind_machine_label
// ---------------------------------------------------------------------------------------------

export const bindMachineLabelPayload = z.object({
  equipment_id: id,
  qr_payload: payload,
  entry,
  photo_id: id,
})
export type BindMachineLabelPayload = z.infer<typeof bindMachineLabelPayload>

// ---------------------------------------------------------------------------------------------
// F6.1 open_run / cancel_run
// ---------------------------------------------------------------------------------------------

export const openRunPayload = z.object({
  /** = the command id; process_runs.id. */
  run_id: id,
  /** PR-yymmdd-<machine>-<n>; the server keeps it when unique, else renumbers n. */
  code_proposed: z.string().regex(/^PR-\d{6}-[A-Z0-9-]+-\d+$/),
  equipment_id: id,
  /** The scanned machine label (V1: picked from the list, then its label scanned to confirm). */
  equipment_qr_payload: payload.nullable(),
  step_code: z.string().min(1),
  /** One scanned bag per lot (the origin lot first). */
  lot_bag_qr_payloads: z.array(payload).min(1),
  /** The phone's reading of those bags' lots; the server re-derives them. */
  lot_unit_ids: z.array(id).min(1),
  /** The settings row; null in V1 (no trial-run settings: the server opens the run without one). */
  yield_band_id: id.nullable(),
  /** The phone's decisions; the server can only turn false into true. */
  flush_required: z.boolean(),
  allergen_clean_required: z.boolean(),
  /** Online at open (flush can be skipped only then). */
  phone_online: z.boolean(),
  previous_run_id: id.nullable(),
  /** Sum of the scanned lots' latest net weights (Kilos planned). */
  planned_g: grams.nonnegative(),
})
export type OpenRunPayload = z.infer<typeof openRunPayload>

export const cancelRunPayload = z.object({
  run_id: id,
  /** Null in V1: one confirm, no reason asked (the server takes the reason as optional). */
  reason: z.enum(CANCEL_REASONS).nullable(),
})
export type CancelRunPayload = z.infer<typeof cancelRunPayload>

// ---------------------------------------------------------------------------------------------
// F6.10 issue_to_run / close_issue
// ---------------------------------------------------------------------------------------------

export const issueToRunPayload = z.object({
  /** = the command id; shipment_items.id. */
  item_id: id,
  shipment_id: id,
  run_id: id,
  container_id: id,
  qr_payload: payload,
  entry,
  /** What the scale showed, bag and sack together. */
  weight_read_g: grams.positive(),
  /** Phone's copy of the sack tare; the server uses containers.tare_kg. */
  tare_g: grams.nonnegative(),
  /** Outside 5–60 kg and typed twice. */
  confirmed_twice: z.boolean(),
  /** Once per lot; needed when the step is Dry. */
  moisture_pct: pctString.nullable(),
})
export type IssueToRunPayload = z.infer<typeof issueToRunPayload>

export const closeIssuePayload = z.object({
  shipment_id: id,
  bags_done: z.number().int().nonnegative(),
  /** Bags of the task not found (they keep their own problem). */
  missing_container_ids: z.array(id),
})
export type CloseIssuePayload = z.infer<typeof closeIssuePayload>

// ---------------------------------------------------------------------------------------------
// F6.2 confirm_cleanout
// ---------------------------------------------------------------------------------------------

export const confirmCleanoutPayload = z.object({
  run_id: id,
  /** In the order of the step's photo points (hopper, drum, outlet for a decorticator). */
  photo_ids: z.array(id).min(1),
  photo_points: z.array(z.string().min(1)).min(1),
  allergen_clean_required: z.boolean(),
  allergen_clean: z.boolean(),
  allergen_photo_id: id.nullable(),
})
export type ConfirmCleanoutPayload = z.infer<typeof confirmCleanoutPayload>

// ---------------------------------------------------------------------------------------------
// F6.3 / F7.3 weigh_in
// ---------------------------------------------------------------------------------------------

export const weighInPayload = z.object({
  /** = the command id; run_inputs.id. */
  run_input_id: id,
  run_id: id,
  qr_payload: payload,
  entry,
  /** The phone's match (the server re-derives it from the code). */
  container_id: id.nullable(),
  /**
   * Machine run: only when the phone had not seen the bag's issue (context WEIGH_IN).
   * Pack run: always — the sack's scale reading at tip-in.
   */
  weight_read_g: grams.positive().nullable(),
  /** The phone had seen this bag's issue to the run (its issue weight is the input weight). */
  issue_seen: z.boolean(),
  confirmed_twice: z.boolean(),
})
export type WeighInPayload = z.infer<typeof weighInPayload>

// ---------------------------------------------------------------------------------------------
// F6.4 report_mixing
// ---------------------------------------------------------------------------------------------

export const reportMixingPayload = z.object({
  run_id: id,
  reason_code: z.enum(MIXING_REASONS),
  note: z.string().max(200).nullable(),
})
export type ReportMixingPayload = z.infer<typeof reportMixingPayload>

// ---------------------------------------------------------------------------------------------
// F6.5 / F6.2 / F7.3 record_output, void_output, close_run
// ---------------------------------------------------------------------------------------------

export const recordOutputPayload = z.object({
  /** = the command id; containers.id. */
  container_id: id,
  /** New or existing draft output unit of this run (one per output and grade). */
  unit_id: id,
  run_id: id,
  qr_payload: payload,
  entry,
  /** FLUSH, an output key of the step registry, or LEFTOVER / SPILLAGE on a pack run. */
  stream_key: z.string().min(1),
  grade: z.string().nullable(),
  sack_type_id: id,
  tare_g: grams.nonnegative(),
  weight_read_g: grams.positive(),
  confirmed_twice: z.boolean(),
  moisture_pct: pctString.nullable(),
  /** The tie the phone told the operator to tie. */
  tie: z.enum(['CONV', 'NONE']),
})
export type RecordOutputPayload = z.infer<typeof recordOutputPayload>

export const voidOutputPayload = z.object({
  run_id: id,
  container_id: id,
  reason: z.enum(['WRONG_OUTPUT', 'WRONG_WEIGHT', 'TAG_PROBLEM', 'OTHER']),
})
export type VoidOutputPayload = z.infer<typeof voidOutputPayload>

export const closeRunPayload = z.object({
  run_id: id,
  /** The 'Machine stopped' time. */
  ended_at: isoTs,
  /** Outputs confirmed as 'nothing came out'. */
  zero_streams: z.array(z.string().min(1)),
  /** Settings typed on the Run screen (keys from the step registry). */
  params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  /** The phone's provisional figures, for comparison only. */
  phone_totals: z.object({
    input_g: grams.nonnegative(),
    output_g: grams.nonnegative(),
    bags_in: z.number().int().nonnegative(),
    sacks_out: z.number().int().nonnegative(),
  }),
})
export type CloseRunPayload = z.infer<typeof closeRunPayload>

// ---------------------------------------------------------------------------------------------
// F6.7 / F7-B1.b seal_sample
// ---------------------------------------------------------------------------------------------

export const sealSamplePayload = z.object({
  /** = the command id; samples.id. */
  sample_id: id,
  kind: z.literal('BATCH_QC'),
  subject_type: z.literal('UNIT'),
  subject_id: id,
  seal_qr_payload: payload,
  entry,
  weight_g: grams.positive(),
  /** Null in V1: the seal scan alone ties the sample to the batch (no sample photo). */
  photo_id: id.nullable(),
  /** PACKING = the batch's packing sample; CONFIRMING = a fresh sample of a held batch. */
  purpose: z.enum(['PACKING', 'CONFIRMING']),
})
export type SealSamplePayload = z.infer<typeof sealSamplePayload>

// ---------------------------------------------------------------------------------------------
// F6-B1.b correct_run (supervisor's own phone and login)
// ---------------------------------------------------------------------------------------------

const correctRunBase = {
  /** = the command id. */
  correction_id: id,
  run_id: id,
  exception_id: id.nullable(),
  reason_code: z.enum(RUN_CAUSES),
}

/** A sack or bag correction: the tag is always there. */
export const sackCorrectionPayload = z
  .object({
    ...correctRunBase,
    kind: z.enum(CORRECTION_KINDS),
    qr_payload: payload,
    entry,
    /** The sack or bag the phone matched (null for a new sack). */
    container_id: id.nullable(),
    /** ADD_OUTPUT: the new sack's container id and its unit (existing output or new). */
    new_container_id: id.nullable(),
    new_unit_id: id.nullable(),
    stream_key: z.string().nullable(),
    sack_type_id: id.nullable(),
    weight_read_g: grams.positive().nullable(),
  })
  .refine(
    (c) =>
      c.kind === 'REWEIGH_OUTPUT' || c.kind === 'ADD_OUTPUT' ? c.weight_read_g !== null : true,
    {
      message: 'weight_read_g is needed to re-weigh or add a sack',
    },
  )
  .refine(
    (c) =>
      c.kind === 'ADD_OUTPUT' ? !!c.new_container_id && !!c.stream_key && !!c.sack_type_id : true,
    {
      message: 'a new sack needs its id, output and sack type',
    },
  )
export type CorrectRunPayload = z.infer<typeof sackCorrectionPayload>

/** POUCH_COUNT: a closed PACK run's good pouches (and label counts), no tag or sack. */
export const pouchCountCorrectionPayload = z.object({
  ...correctRunBase,
  kind: z.literal('POUCH_COUNT'),
  pouches_good: z.number().int().min(0),
  labels_spoiled: z.number().int().min(0).optional(),
  labels_destroyed: z.number().int().min(0).optional(),
})
export type PouchCountCorrectionPayload = z.infer<typeof pouchCountCorrectionPayload>

/** correct_run as sent: a sack correction (tag required) or a pouch count (no tag). */
export const correctRunPayload = z.union([sackCorrectionPayload, pouchCountCorrectionPayload])
export type CorrectRunCommandPayload = z.infer<typeof correctRunPayload>

// ---------------------------------------------------------------------------------------------
// F6-B1.c decide_run (supervisor's own phone and login): close a held run's balance problem
// ---------------------------------------------------------------------------------------------

/** The decisions a supervisor makes on the phone (moving the outputs to ordinary stays on the console). */
export const RUN_DECISIONS = ['ACCEPT_SHORTFALL', 'CORRECTED'] as const
export type RunDecision = (typeof RUN_DECISIONS)[number]

export const decideRunPayload = z.object({
  run_id: id,
  /** The run's open BALANCE_OUT_OF_BAND problem. */
  exception_id: id,
  reason_code: z.enum(RUN_CAUSES),
  decision: z.enum(RUN_DECISIONS),
  note: z.string().max(500).nullable(),
})
export type DecideRunPayload = z.infer<typeof decideRunPayload>

registerCommand('bind_machine_label', bindMachineLabelPayload)
registerCommand('open_run', openRunPayload)
registerCommand('cancel_run', cancelRunPayload)
registerCommand('issue_to_run', issueToRunPayload)
registerCommand('close_issue', closeIssuePayload)
registerCommand('confirm_cleanout', confirmCleanoutPayload)
registerCommand('weigh_in', weighInPayload)
registerCommand('report_mixing', reportMixingPayload)
registerCommand('record_output', recordOutputPayload)
registerCommand('void_output', voidOutputPayload)
registerCommand('close_run', closeRunPayload)
registerCommand('seal_sample', sealSamplePayload)
registerCommand('correct_run', correctRunPayload)
registerCommand('decide_run', decideRunPayload)

export const PROCESS_COMMAND_TYPES = [
  'bind_machine_label',
  'open_run',
  'cancel_run',
  'issue_to_run',
  'close_issue',
  'confirm_cleanout',
  'weigh_in',
  'report_mixing',
  'record_output',
  'void_output',
  'close_run',
  'seal_sample',
  'correct_run',
  'decide_run',
] as const
